import React, { useMemo, useState, useEffect, useCallback, memo } from 'react';
import MiniConsensusChart from './MiniConsensusChart';
import BettingPoolButton from '../bettingPools/BettingPoolButton';
import { auth, db } from '../../../firebase';
import { doc, setDoc, getDoc, getDocs, collection, increment, serverTimestamp } from 'firebase/firestore';

const GameMarketView = ({ gameMarkets }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedGames, setExpandedGames] = useState(new Set());
  const [sortBy, setSortBy] = useState('volume');
  const [showPast, setShowPast] = useState(false);
  const [userVotes, setUserVotes] = useState({});
  const [voteCounts, setVoteCounts] = useState({});
  const [userBets, setUserBets] = useState({});
  const itemsPerPage = 15;

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  const isGamePast = (timestamp) => {
    // ALWAYS treat games without timestamps as past/incomplete data
    // These should be hidden by default
    if (!timestamp) {
      return true;
    }

    // Normal timestamp check for games WITH timestamps
    const now = new Date();
    const gameTime = new Date(timestamp);
    if (!isNaN(gameTime.getTime())) {
      return gameTime < now;
    }

    // If we can't parse the timestamp, hide it
    return true;
  };

  const sortedGames = useMemo(() => {
    if (!gameMarkets || gameMarkets.length === 0) return [];
    let filtered = [...gameMarkets];
    if (!showPast) {
      filtered = filtered.filter(game => !isGamePast(game.gameTimestamp));
    }
    return filtered.sort((a, b) => {
      switch (sortBy) {
        case 'volume':
          return (b.totalPredictions || b.totalPicks || 0) - (a.totalPredictions || a.totalPicks || 0);
        case 'gametime':
          const gameTimeA = a.gameTimestamp || a.timestamp || 0;
          const gameTimeB = b.gameTimestamp || b.timestamp || 0;
          if (!gameTimeA && !gameTimeB) return 0;
          if (!gameTimeA) return 1;
          if (!gameTimeB) return -1;
          return gameTimeA - gameTimeB;
        case 'recency':
          const timeA = a.lastPredictionTime || a.timestamp || 0;
          const timeB = b.lastPredictionTime || b.timestamp || 0;
          return timeB - timeA;
        case 'confidence':
          const confA = a.consensusConfidenceNumeric || a.avgConfidenceNumeric || 0;
          const confB = b.consensusConfidenceNumeric || b.avgConfidenceNumeric || 0;
          return confB - confA;
        case 'pool':
          const poolA = a.poolData ? (a.poolData.homePot + a.poolData.awayPot) : 0;
          const poolB = b.poolData ? (b.poolData.homePot + b.poolData.awayPot) : 0;
          return poolB - poolA;
        default:
          return (b.totalPredictions || b.totalPicks || 0) - (a.totalPredictions || a.totalPicks || 0);
      }
    });
  }, [gameMarkets, sortBy, showPast]);

  const totalPages = Math.ceil(sortedGames.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentGames = sortedGames.slice(startIndex, endIndex);

  useEffect(() => {
    if (gameMarkets && gameMarkets.length > 0) {
      const gamesWithoutDates = gameMarkets.filter(game => !game.gameTimestamp);
      if (gamesWithoutDates.length > 0) {
        console.log('Games without timestamps:', gamesWithoutDates.length);
      }
    }
  }, [gameMarkets]);

  const getConfidenceColor = (confidenceValue) => {
    if (typeof confidenceValue === 'number') {
      if (confidenceValue >= 70) return 'text-emerald-400';
      if (confidenceValue >= 40) return 'text-amber-400';
      return 'text-red-400';
    }
    switch(confidenceValue) {
      case 'HIGH': return 'text-emerald-400';
      case 'MED': return 'text-amber-400';
      case 'LOW': return 'text-red-400';
      default: return 'text-zinc-500';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Recent';
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 60) return `${minutes}m`;
    if (hours < 24) return `${hours}h`;
    return `${days}d`;
  };

  const formatGameDate = (timestamp, dateKey) => {
    if (timestamp) {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        const etDate = date.toLocaleDateString('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric'
        });
        const etTime = date.toLocaleTimeString('en-US', {
          timeZone: 'America/New_York',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return `${etDate} ${etTime}`;
      }
    }
    if (dateKey) {
      const [year, month, day] = dateKey.split('-');
      const date = new Date(year, month - 1, day);
      if (!isNaN(date.getTime())) {
        const etDate = date.toLocaleDateString('en-US', {
          timeZone: 'America/New_York',
          month: 'short',
          day: 'numeric'
        });
        return etDate;
      }
    }
    return '-';
  };

  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  }, [totalPages]);

  const toggleGameExpansion = useCallback((gameId) => {
    setExpandedGames(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(gameId)) {
        newExpanded.delete(gameId);
      } else {
        newExpanded.add(gameId);
      }
      return newExpanded;
    });
  }, []);

  useEffect(() => {
    const loadUserBets = async () => {
      if (!auth.currentUser) return;
      try {
        const bettingHistoryRef = collection(db, 'users', auth.currentUser.uid, 'bettingHistory');
        const bettingHistorySnapshot = await getDocs(bettingHistoryRef);
        const bets = {};
        bettingHistorySnapshot.forEach(doc => {
          const betData = doc.data();
          if (betData.poolId || betData.gameId) {
            const poolId = betData.poolId || betData.gameId;
            bets[poolId] = {
              team: betData.bet?.team,
              amount: betData.bet?.amount,
              status: betData.status || 'pending'
            };
          }
        });
        setUserBets(bets);
      } catch (err) {
        console.error('Error loading user bets:', err);
      }
    };
    loadUserBets();
  }, [auth.currentUser?.uid]);

  // Create stable game IDs string to prevent re-fetching
  const currentGameIds = useMemo(() =>
    currentGames.map(g => g.gameId).filter(Boolean).join(','),
    [currentGames]
  );

  useEffect(() => {
    if (!gameMarkets || gameMarkets.length === 0) return;
    const loadVotes = async () => {
      try {
        const gameIds = currentGameIds.split(',').filter(Boolean);
        if (gameIds.length === 0) return;
        const counts = {};
        const chunkSize = 10;
        for (let i = 0; i < gameIds.length; i += chunkSize) {
          const chunk = gameIds.slice(i, i + chunkSize);
          try {
            const promises = chunk.map(gameId =>
              getDoc(doc(db, 'marketVoteCounts', gameId))
                .then(doc => ({ gameId, data: doc.exists() ? doc.data() : null }))
                .catch(() => ({ gameId, data: null }))
            );
            const results = await Promise.all(promises);
            results.forEach(({ gameId, data }) => {
              if (data) counts[gameId] = data;
            });
          } catch (err) {}
        }
        setVoteCounts(counts);
        if (auth.currentUser) {
          try {
            const userVoteDoc = await getDoc(doc(db, 'marketVotes', auth.currentUser.uid));
            if (userVoteDoc.exists()) {
              const allUserVotes = userVoteDoc.data().votes || {};
              const relevantVotes = {};
              gameIds.forEach(id => {
                if (allUserVotes[id]) relevantVotes[id] = allUserVotes[id];
              });
              setUserVotes(relevantVotes);
            }
          } catch (err) {}
        }
      } catch (error) {
        console.error('Error loading votes:', error);
      }
    };
    loadVotes();
  }, [currentGameIds, gameMarkets]);

  const handleVote = useCallback(async (e, gameId, voteType) => {
    e.stopPropagation();
    if (!auth.currentUser) {
      alert('Please sign in to vote');
      return;
    }
    const userId = auth.currentUser.uid;
    const currentVote = userVotes[gameId];
    try {
      if (currentVote === voteType) {
        const newUserVotes = { ...userVotes };
        delete newUserVotes[gameId];
        await setDoc(doc(db, 'marketVotes', userId), {
          votes: newUserVotes,
          lastUpdated: serverTimestamp()
        });
        await setDoc(doc(db, 'marketVoteCounts', gameId), {
          upvotes: increment(voteType === 'up' ? -1 : 0),
          downvotes: increment(voteType === 'down' ? -1 : 0),
          lastUpdated: serverTimestamp()
        }, { merge: true });
        setUserVotes(newUserVotes);
        setVoteCounts(prev => ({
          ...prev,
          [gameId]: {
            ...prev[gameId],
            [voteType === 'up' ? 'upvotes' : 'downvotes']:
              Math.max(0, (prev[gameId]?.[voteType === 'up' ? 'upvotes' : 'downvotes'] || 0) - 1)
          }
        }));
      } else {
        const newUserVotes = { ...userVotes, [gameId]: voteType };
        await setDoc(doc(db, 'marketVotes', userId), {
          votes: newUserVotes,
          lastUpdated: serverTimestamp()
        });
        const updates = {
          upvotes: increment(voteType === 'up' ? 1 : (currentVote === 'up' ? -1 : 0)),
          downvotes: increment(voteType === 'down' ? 1 : (currentVote === 'down' ? -1 : 0)),
          lastUpdated: serverTimestamp()
        };
        await setDoc(doc(db, 'marketVoteCounts', gameId), updates, { merge: true });
        setUserVotes(newUserVotes);
        setVoteCounts(prev => {
          const current = prev[gameId] || { upvotes: 0, downvotes: 0 };
          const newCounts = {
            upvotes: current.upvotes + (voteType === 'up' ? 1 : 0) - (currentVote === 'up' ? 1 : 0),
            downvotes: current.downvotes + (voteType === 'down' ? 1 : 0) - (currentVote === 'down' ? 1 : 0)
          };
          return { ...prev, [gameId]: newCounts };
        });
      }
    } catch (error) {
      console.error('Error voting:', error);
      alert('Failed to save vote. Please try again.');
    }
  }, [userVotes, voteCounts]);

  if (!sortedGames || sortedGames.length === 0) {
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-xs font-black uppercase tracking-wider text-zinc-400">LIVE PREDICTIONS</h2>
          <span className="text-[10px] text-zinc-600">Real-time community predictions</span>
        </div>
        <div className="text-center py-8 text-zinc-600 text-xs uppercase tracking-wider">
          No game predictions in selected timeframe
        </div>
      </div>
    );
  }

  // Desktop Row Component - BLOOMBERG TERMINAL STYLE
  const DesktopGameRow = memo(({ game }) => {
    const isExpanded = expandedGames.has(game.gameId);
    const userVote = userVotes[game.gameId];
    const voteCount = voteCounts[game.gameId] || { upvotes: 0, downvotes: 0 };
    const isPast = isGamePast(game.gameTimestamp);

    // Calculate momentum indicator
    const momentum = game.homePercentage > 50 ? game.homePercentage - 50 : -(game.awayPercentage - 50);
    const momentumColor = momentum > 20 ? 'text-emerald-400' : momentum < -20 ? 'text-red-400' : 'text-zinc-500';

    return (
      <div className={`border-b border-zinc-900/50 ${isPast ? 'opacity-40' : ''} hover:bg-zinc-950/30 transition-colors`}>
        <div className="px-2 py-1">
          <div className="grid grid-cols-[32px_70px_140px_28px_140px_120px_50px_50px_50px_50px_50px_50px_100px_140px] gap-1 items-center text-[11px]">

            {/* Col 1: Sport Ticker */}
            <div className="font-mono text-[10px] text-zinc-500">
              {game.sport?.substring(0, 3).toUpperCase()}
            </div>

            {/* Col 2: Game Time */}
            <div className="font-mono text-zinc-600 text-[10px]">
              {formatGameDate(game.gameTimestamp, game.dateKey)}
            </div>

            {/* Col 3: Home Team */}
            <div className={`font-mono text-right pr-1 ${game.consensus === game.home ? 'text-violet-400 font-bold' : 'text-zinc-400'}`}>
              {game.home.toUpperCase()}
            </div>

            {/* Col 4: VS */}
            <div className="text-center font-mono text-[9px] text-zinc-700">
              VS
            </div>

            {/* Col 5: Away Team */}
            <div className={`font-mono text-left pl-1 ${game.consensus === game.away ? 'text-violet-400 font-bold' : 'text-zinc-400'}`}>
              {game.away.toUpperCase()}
            </div>

            {/* Col 6: Market Sentiment Bar + Percentages */}
            <div className="flex items-center gap-1">
              <span className="font-mono text-[10px] text-emerald-400 w-8 text-right">{Math.round(game.homePercentage)}%</span>
              <div className="w-12 h-1 bg-zinc-900 relative overflow-hidden">
                <div className="absolute left-0 top-0 h-full bg-emerald-400/80" style={{ width: `${game.homePercentage}%` }} />
                <div className="absolute left-0 top-0 h-full bg-red-400/80" style={{ left: `${game.homePercentage}%`, width: `${game.awayPercentage}%` }} />
              </div>
              <span className="font-mono text-[10px] text-red-400 w-8">{Math.round(game.awayPercentage)}%</span>
            </div>

            {/* Col 7: Quick Pick Activation */}
            <div className="text-center">
              <div className="font-mono text-zinc-300 font-bold">{game.totalPredictions || 0}</div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">QPA</div>
            </div>

            {/* Col 8: Confidence */}
            <div className="text-center">
              <div className={`font-mono font-bold ${getConfidenceColor(game.consensusConfidenceNumeric)}`}>
                {game.consensusConfidenceNumeric ? `${Math.round(game.consensusConfidenceNumeric)}` : '--'}
              </div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">CONF</div>
            </div>

            {/* Col 9: Pool Size */}
            <div className="text-center">
              <div className="font-mono text-amber-400 font-bold">
                {game.poolData ? (game.poolData.homePot + game.poolData.awayPot) : 0}
              </div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">POOL</div>
            </div>

            {/* Col 10: Implied Odds */}
            <div className="text-center">
              <div className="font-mono text-violet-400 font-bold">
                {(() => {
                  if (!game.poolData || !game.consensus) return '--';
                  const totalPot = game.poolData.homePot + game.poolData.awayPot;
                  if (totalPot === 0) return '--';
                  const winningPot = game.consensus === game.home ? game.poolData.homePot : game.poolData.awayPot;
                  if (winningPot === 0) return '∞';
                  return `${(totalPot / winningPot).toFixed(1)}x`;
                })()}
              </div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">ODDS</div>
            </div>

            {/* Col 11: Crowd % */}
            <div className="text-center">
              <div className="font-mono text-zinc-300 font-bold">
                {(() => {
                  const pool = game.poolData;

                  // If pool exists with money, show betting distribution
                  if (pool && (pool.homePot > 0 || pool.awayPot > 0)) {
                    const totalPot = pool.homePot + pool.awayPot;
                    if (totalPot === 0) return '--';

                    // Get pot for consensus team
                    const consensusPot = game.consensus === game.home ? pool.homePot : pool.awayPot;
                    const percentage = Math.round((consensusPot / totalPot) * 100);

                    // Handle edge cases
                    if (percentage === 0) return '0';  // All money against consensus
                    if (percentage === 100) return 'ALL';  // All money on consensus
                    return `${percentage}`;
                  }

                  // Otherwise use prediction counts
                  if (game.consensus && game.totalPredictions > 0) {
                    const consensusPicks = game.consensus === game.home ? game.homePicks : game.awayPicks;
                    const percentage = Math.round((consensusPicks / game.totalPredictions) * 100);

                    if (percentage === 0) return '0';  // No picks for consensus (shouldn't happen)
                    if (percentage === 100) return 'ALL';  // All picks for consensus
                    return `${percentage}`;
                  }

                  return '--';
                })()}
              </div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">CRWD</div>
            </div>

            {/* Col 12: Momentum Indicator */}
            <div className="text-center">
              <div className={`font-mono font-bold ${momentumColor}`}>
                {momentum > 0 ? '↑' : momentum < 0 ? '↓' : '→'} {Math.abs(momentum).toFixed(0)}
              </div>
              <div className="text-[8px] text-zinc-700 uppercase tracking-wider">MOM</div>
            </div>

            {/* Col 13: Consensus Pick */}
            <div className="flex items-center justify-center">
              <div className="px-2 py-0.5 bg-violet-500/10 border border-violet-500/30">
                <span className="font-mono text-[10px] font-black text-violet-400 uppercase tracking-wide">
                  {game.consensus}
                </span>
              </div>
            </div>

            {/* Col 14: Actions */}
            <div className="flex items-center justify-end gap-0.5">
              {game.sport?.toLowerCase() === 'football' ? (
                <div className="[&>.betting-pool-btn]:!px-2 [&>.betting-pool-btn]:!py-0.5 [&>.betting-pool-btn]:!text-[9px] [&>.betting-pool-btn]:!font-mono [&>.betting-pool-btn]:!gap-1 [&>.betting-pool-btn_svg]:!w-3 [&>.betting-pool-btn_svg]:!h-3">
                  <BettingPoolButton
                    game={game}
                    userBet={userBets[`football_${game.gameId}`]}
                  />
                </div>
              ) : (
                <button className="px-2 py-0.5 text-[9px] font-mono bg-zinc-900/50 border border-zinc-800 text-zinc-600" disabled>
                  BET
                </button>
              )}
              <button
                className={`px-1.5 py-0.5 border text-[9px] font-mono transition-all
                  ${!auth.currentUser
                    ? 'border-zinc-800 text-zinc-700 cursor-not-allowed opacity-50'
                    : userVote === 'up'
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                    : 'border-zinc-800 text-zinc-600 hover:border-emerald-500/30 hover:text-emerald-500'}`}
                onClick={(e) => handleVote(e, game.gameId, 'up')}
                disabled={!auth.currentUser}
                title={!auth.currentUser ? 'Sign in to vote' : ''}
              >
                ↑{voteCount.upvotes}
              </button>
              <button
                className={`px-1.5 py-0.5 border text-[9px] font-mono transition-all
                  ${!auth.currentUser
                    ? 'border-zinc-800 text-zinc-700 cursor-not-allowed opacity-50'
                    : userVote === 'down'
                    ? 'bg-red-500/20 border-red-500/50 text-red-400'
                    : 'border-zinc-800 text-zinc-600 hover:border-red-500/30 hover:text-red-500'}`}
                onClick={(e) => handleVote(e, game.gameId, 'down')}
                disabled={!auth.currentUser}
                title={!auth.currentUser ? 'Sign in to vote' : ''}
              >
                ↓{voteCount.downvotes}
              </button>
              <button
                className={`px-1.5 py-0.5 border text-[9px] font-mono transition-all cursor-pointer
                  ${isExpanded
                    ? 'bg-violet-500/20 border-violet-500/50 text-violet-400'
                    : 'border-zinc-800 text-zinc-600 hover:text-white hover:border-zinc-600'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Expand button clicked for game:', game.gameId);
                  toggleGameExpansion(game.gameId);
                }}
                title={isExpanded ? 'Collapse chart' : 'Expand chart'}
              >
                {isExpanded ? '−' : '+'}
              </button>
            </div>
          </div>
        </div>

        {/* Expanded Chart */}
        {isExpanded && (
          <div className="border-t border-zinc-900/50 bg-zinc-950/50 p-2">
            {game.predictions && Array.isArray(game.predictions) && game.predictions.length > 0 ? (
              <MiniConsensusChart
                predictions={game.predictions}
                home={game.home}
                away={game.away}
                homePercentage={game.homePercentage}
                awayPercentage={game.awayPercentage}
                timeRange="7d"
              />
            ) : (
              <div className="text-center py-4 text-zinc-600 text-xs">
                No prediction history available
              </div>
            )}
          </div>
        )}
      </div>
    );
  });

  // Mobile Card Component - PREMIUM COMPACT
  const MobileGameCard = ({ game }) => {
    const isExpanded = expandedGames.has(game.gameId);
    const userVote = userVotes[game.gameId];
    const voteCount = voteCounts[game.gameId] || { upvotes: 0, downvotes: 0 };
    const isPast = isGamePast(game.gameTimestamp);

    return (
      <div className={`bg-black border border-zinc-900 ${isPast ? 'opacity-50' : ''}`}>
        <div className="p-3">
          {/* Header - Cleaner */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono bg-zinc-900 px-1.5 py-0.5 text-zinc-400">
                {game.sport?.substring(0, 3).toUpperCase()}
              </span>
              <span className="text-[10px] font-mono text-zinc-600">
                {formatGameDate(game.gameTimestamp, game.dateKey)}
              </span>
            </div>
            <span className="text-[9px] font-mono text-zinc-600">
              {formatTimeAgo(game.lastPredictionTime)} AGO
            </span>
          </div>

          {/* Match & Consensus */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-sm font-mono font-bold ${game.consensus === game.home ? 'text-violet-400' : 'text-zinc-500'}`}>
                {game.home}
              </span>
              <span className="text-[10px] font-mono text-zinc-700">VS</span>
              <span className={`text-sm font-mono font-bold ${game.consensus === game.away ? 'text-violet-400' : 'text-zinc-500'}`}>
                {game.away}
              </span>
            </div>

            {/* Clean Market Bar */}
            <div className="h-3 bg-zinc-950 border border-zinc-800 relative overflow-hidden">
              <div className="absolute left-0 top-0 h-full bg-emerald-500/80" style={{ width: `${game.homePercentage}%` }} />
              <div className="absolute left-0 top-0 h-full bg-red-500/80" style={{ left: `${game.homePercentage}%`, width: `${game.awayPercentage}%` }} />
              <div className="absolute inset-0 flex items-center justify-between px-1">
                <span className="text-[9px] font-mono text-white font-bold">{Math.round(game.homePercentage)}%</span>
                <span className="text-[9px] font-mono text-white font-bold">{Math.round(game.awayPercentage)}%</span>
              </div>
            </div>
          </div>

          {/* Stats Row - Horizontal */}
          <div className="flex items-center justify-between mb-3">
            <div className="text-center">
              <div className="text-xs font-mono font-bold text-zinc-300">{game.totalPredictions || 0}</div>
              <div className="text-[8px] text-zinc-600">QPA</div>
            </div>
            <div className="text-center">
              <div className={`text-xs font-mono font-bold ${getConfidenceColor(game.consensusConfidenceNumeric)}`}>
                {game.consensusConfidenceNumeric ? `${Math.round(game.consensusConfidenceNumeric)}%` : '-'}
              </div>
              <div className="text-[8px] text-zinc-600">CONF</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-mono font-bold text-amber-400">
                {game.poolData ? (game.poolData.homePot + game.poolData.awayPot) : 0}
              </div>
              <div className="text-[8px] text-zinc-600">POOL</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-mono font-bold text-violet-400">
                {(() => {
                  if (!game.poolData || !game.consensus) return '-';
                  const totalPot = game.poolData.homePot + game.poolData.awayPot;
                  if (totalPot === 0) return '-';
                  const winningPot = game.consensus === game.home ? game.poolData.homePot : game.poolData.awayPot;
                  if (winningPot === 0) return '∞';
                  return `${(totalPot / winningPot).toFixed(1)}x`;
                })()}
              </div>
              <div className="text-[8px] text-zinc-600">ODDS</div>
            </div>
            <div className="text-center">
              <div className="text-xs font-mono font-bold text-zinc-300">
                {(() => {
                  const pool = game.poolData;
                  if (!pool || (!pool.homePot && !pool.awayPot)) {
                    if (game.consensus && game.totalPredictions > 0) {
                      const consensusPicks = game.consensus === game.home ? game.homePicks : game.awayPicks;
                      return `${Math.round((consensusPicks / game.totalPredictions) * 100)}%`;
                    }
                    return '-';
                  }
                  const totalPot = pool.homePot + pool.awayPot;
                  const consensusPot = game.consensus === game.home ? pool.homePot : pool.awayPot;
                  return `${Math.round((consensusPot / totalPot) * 100)}%`;
                })()}
              </div>
              <div className="text-[8px] text-zinc-600">CRWD</div>
            </div>
          </div>

          {/* Winner & Actions */}
          <div className="flex items-center justify-between">
            <div className="px-2 py-1 bg-violet-600/10 border border-violet-600/30">
              <span className="text-[10px] font-mono font-black text-violet-400 uppercase">
                {game.consensus}
              </span>
            </div>

            {/* Actions - Clean Buttons */}
            <div className="flex items-center gap-1">
            {game.sport?.toLowerCase() === 'football' ? (
              <div className="flex-1 [&>.betting-pool-btn]:!px-1 [&>.betting-pool-btn]:!py-0.5 [&>.betting-pool-btn]:!text-[8px] [&>.betting-pool-btn]:!font-mono [&>.betting-pool-btn]:!gap-0.5 [&>.betting-pool-btn_svg]:!w-2.5 [&>.betting-pool-btn_svg]:!h-2.5">
                <BettingPoolButton
                  game={game}
                  userBet={userBets[`football_${game.gameId}`]}
                />
              </div>
            ) : (
              <button className="flex-1 px-1 py-0.5 text-[8px] font-mono bg-zinc-900/50 border border-zinc-800 text-zinc-600" disabled>
                BET
              </button>
            )}
            <button
              className={`px-1 py-0.5 border text-[8px] font-mono
                ${!auth.currentUser
                  ? 'border-zinc-800 text-zinc-700 opacity-50'
                  : userVote === 'up'
                  ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                  : 'border-zinc-800 text-zinc-600'}`}
              onClick={(e) => handleVote(e, game.gameId, 'up')}
              disabled={!auth.currentUser}
            >
              ↑{voteCount.upvotes}
            </button>
            <button
              className={`px-1 py-0.5 border text-[8px] font-mono
                ${!auth.currentUser
                  ? 'border-zinc-800 text-zinc-700 opacity-50'
                  : userVote === 'down'
                  ? 'bg-red-500/20 border-red-500/50 text-red-400'
                  : 'border-zinc-800 text-zinc-600'}`}
              onClick={(e) => handleVote(e, game.gameId, 'down')}
              disabled={!auth.currentUser}
            >
              ↓{voteCount.downvotes}
            </button>
            <button
              className={`px-1 py-0.5 border text-[8px] font-mono
                ${isExpanded
                  ? 'bg-violet-500/20 border-violet-500/50 text-violet-400'
                  : 'border-zinc-800 text-zinc-600'}`}
              onClick={(e) => {
                e.stopPropagation();
                toggleGameExpansion(game.gameId);
              }}
            >
              {isExpanded ? '−' : '+'}
            </button>
            </div>
          </div>
        </div>

        {isExpanded && game.predictions && Array.isArray(game.predictions) && game.predictions.length > 0 && (
          <div className="border-t border-zinc-900">
            <MiniConsensusChart
              predictions={game.predictions}
              home={game.home}
              away={game.away}
              homePercentage={game.homePercentage}
              awayPercentage={game.awayPercentage}
              timeRange="7d"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xs font-black uppercase tracking-wider text-zinc-400">LIVE PREDICTIONS</h2>
          <span className="text-[10px] text-zinc-600">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedGames.length)} of {sortedGames.length} predictions
          </span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          <span className="text-[10px] font-bold text-zinc-500 uppercase flex-shrink-0">Sort:</span>
          <div className="flex gap-[1px] bg-zinc-900 p-[1px] flex-shrink-0">
            {['volume', 'gametime', 'recency', 'confidence', 'pool'].map((option) => (
              <button
                key={option}
                className={`px-2 md:px-3 py-1 text-[9px] md:text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap
                  ${sortBy === option
                    ? 'bg-violet-600 text-white'
                    : 'bg-black text-zinc-500 hover:bg-zinc-900 hover:text-white'}`}
                onClick={() => setSortBy(option)}
              >
                {option === 'gametime' ? 'TIME' : option}
              </button>
            ))}
            <button
              className={`px-2 md:px-3 py-1 text-[9px] md:text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap
                ${showPast
                  ? 'bg-amber-600/80 text-white'
                  : 'bg-black text-zinc-500 hover:bg-zinc-900 hover:text-white'}`}
              onClick={() => setShowPast(!showPast)}
            >
              {showPast ? 'HIDE PAST' : 'SHOW PAST'}
            </button>
          </div>
        </div>
      </div>

      {/* Markets List */}
      <div className="space-y-[1px]">
        {currentGames.map((game, index) =>
          isMobile ? (
            <MobileGameCard key={`${game.gameId}-${index}`} game={game} />
          ) : (
            <DesktopGameRow key={`${game.gameId}-${index}`} game={game} />
          )
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <button
            className="px-3 py-1 border border-zinc-800 text-xs font-bold text-zinc-400
                     hover:border-zinc-600 hover:text-white uppercase disabled:opacity-30"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>
          <div className="flex items-center gap-1">
            {currentPage > 2 && (
              <>
                <button
                  className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-white"
                  onClick={() => handlePageChange(1)}>1</button>
                {currentPage > 3 && <span className="text-zinc-700">...</span>}
              </>
            )}
            {currentPage > 1 && (
              <button
                className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-white"
                onClick={() => handlePageChange(currentPage - 1)}>
                {currentPage - 1}
              </button>
            )}
            <button className="px-3 py-1 text-xs font-bold bg-violet-600 text-white">
              {currentPage}
            </button>
            {currentPage < totalPages && (
              <button
                className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-white"
                onClick={() => handlePageChange(currentPage + 1)}>
                {currentPage + 1}
              </button>
            )}
            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && <span className="text-zinc-700">...</span>}
                <button
                  className="px-2 py-1 text-xs font-bold text-zinc-500 hover:text-white"
                  onClick={() => handlePageChange(totalPages)}>
                  {totalPages}
                </button>
              </>
            )}
          </div>
          <button
            className="px-3 py-1 border border-zinc-800 text-xs font-bold text-zinc-400
                     hover:border-zinc-600 hover:text-white uppercase disabled:opacity-30"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default GameMarketView;